import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeft, Activity, Clock, User } from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';
import { supabase } from '@/lib/supabase';
import { useRealtimeCases } from '@/hooks/useRealtimeCases';
import { IconOrLamp, IconHospitalBed, IconVitalsMonitor } from '@/components/medical/MedicalIcons';

// ============================================================================
// OR Floor Plan + PACU bed map
// ----------------------------------------------------------------------------
// A spatial view over the unified case spine (public.or_cases): one tile per
// operating room (occupied vs available, with the live case) and a PACU bed
// map (capacity-bounded grid of recovering patients). Reads the same realtime
// publication as the live console, so room/bed state tracks case moves within
// ~a second. Org scope is enforced by RLS on the view.
// ============================================================================

interface FloorCase {
  case_id: number;
  case_number: string | null;
  patient_name: string;
  procedure_name: string;
  surgeon_name: string;
  or_room: string;
  case_status: string;
  stage: string;
  schedule_status: string;
  stage_entered_at: string;
  actual_start: string | null;
  scheduled_start: string | null;
  estimated_duration_minutes: number | null;
}

const PACU_CAPACITY = 8;

function elapsed(ts: string | null): string {
  if (!ts) return '—';
  const mins = Math.max(0, Math.floor((Date.now() - new Date(ts).getTime()) / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function clock(ts: string | null): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}

const STATUS_LABEL: Record<string, string> = {
  patient_in_room: 'Patient in Room',
  anesthesia_start: 'Anesthesia',
  prep_start: 'Prep',
  incision: 'Incision',
  in_progress: 'In Progress',
  closing: 'Closing',
  wound_closed: 'Wound Closed',
};

export function FloorPlanRoute() {
  useRealtimeCases([['floor-cases']]);

  const { data: cases = [], isLoading } = useQuery({
    queryKey: ['floor-cases'],
    queryFn: async () => {
      const today = new Date().toISOString().slice(0, 10);
      const { data, error } = await supabase
        .from('or_cases')
        .select(`
          case_id, case_number, patient_name, procedure_name, surgeon_name,
          or_room, case_status, stage, schedule_status, stage_entered_at,
          actual_start, scheduled_start, estimated_duration_minutes
        `)
        .eq('case_date', today)
        .neq('stage', 'cancelled')
        .order('scheduled_start', { ascending: true });
      if (error) throw error;
      return data as FloorCase[];
    },
    refetchInterval: 30000,
  });

  // Rooms derived from today's cases, natural-sorted (OR 2 before OR 10).
  const rooms = Array.from(new Set(cases.map((c) => c.or_room).filter(Boolean)))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  const roomState = (room: string) => {
    const roomCases = cases.filter((c) => c.or_room === room);
    // `stage` is the canonical flow position on the spine; a room is occupied
    // only while a case is in_OR. case_status stays for the granular label.
    const active = roomCases.find((c) => c.stage === 'inor') ?? null;
    const next = roomCases
      .filter((c) => c !== active && c.stage === 'preop' && c.scheduled_start)
      .sort((a, b) => new Date(a.scheduled_start!).getTime() - new Date(b.scheduled_start!).getTime())[0] ?? null;
    return { active, next };
  };

  const pacuCases = cases
    .filter((c) => c.stage === 'pacu')
    .sort((a, b) => new Date(a.stage_entered_at).getTime() - new Date(b.stage_entered_at).getTime());
  const beds = Array.from({ length: Math.max(PACU_CAPACITY, pacuCases.length) }, (_, i) => pacuCases[i] ?? null);

  const inUse = rooms.filter((r) => roomState(r).active).length;

  if (isLoading) {
    return (
      <div className="min-h-full bg-pt-darker flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pt-gold" />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-pt-darker text-pt-text p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-pt-text-muted hover:text-pt-gold transition-colors">
              <ArrowLeft size={20} />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-pt-text">OR Floor Plan</h1>
              <p className="text-pt-text-muted text-sm">Live room utilization &amp; PACU bed map</p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-pt-dark rounded-lg border border-pt-border">
            <Activity className="w-4 h-4 text-pt-green animate-pulse" />
            <span className="text-sm text-pt-text">
              {inUse}/{rooms.length || 0} rooms in use
            </span>
          </div>
        </div>

        {/* Operating Rooms */}
        <h2 className="text-xs font-mono uppercase tracking-widest text-pt-gold mb-4">Operating Rooms</h2>
        {rooms.length === 0 ? (
          <div className="mb-10">
            <EmptyState title="No rooms scheduled today" hint="Rooms appear here once cases are booked onto the schedule." />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-10">
            {rooms.map((room) => {
              const { active, next } = roomState(room);
              const busy = !!active;
              return (
                <div
                  key={room}
                  className={`rounded-lg border p-4 transition-colors ${
                    busy
                      ? 'bg-pt-red/5 border-pt-red/40'
                      : 'bg-pt-dark border-pt-green/25'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <IconOrLamp size={20} className={busy ? 'text-pt-red' : 'text-pt-green'} />
                      <span className="font-semibold text-pt-text">{room}</span>
                    </div>
                    <span
                      className={`text-[10px] font-mono uppercase tracking-wide px-2 py-1 rounded border ${
                        busy
                          ? 'bg-pt-red/20 text-pt-red border-pt-red/30'
                          : 'bg-pt-green/15 text-pt-green border-pt-green/30'
                      }`}
                    >
                      {busy ? 'In Use' : 'Available'}
                    </span>
                  </div>

                  {active ? (
                    <Link to={`/live-cases/${active.case_id}`} className="block group">
                      <p className="text-sm font-semibold text-pt-text group-hover:text-pt-gold transition-colors leading-snug">
                        {active.procedure_name}
                      </p>
                      <div className="flex items-center gap-1.5 mt-2 text-pt-text-muted text-xs">
                        <User className="w-3.5 h-3.5 text-pt-text-dim" />
                        {active.patient_name}
                      </div>
                      <div className="text-xs text-pt-text-dim mt-1">{active.surgeon_name}</div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-pt-border">
                        <span className="text-[11px] font-mono text-pt-amber">
                          {STATUS_LABEL[active.case_status] ?? active.case_status}
                        </span>
                        <span className="flex items-center gap-1 text-[11px] font-mono text-pt-text-muted">
                          <Clock className="w-3 h-3" />
                          {elapsed(active.actual_start ?? active.stage_entered_at)}
                        </span>
                      </div>
                    </Link>
                  ) : (
                    <div className="text-xs text-pt-text-dim">
                      {next ? (
                        <>
                          <span className="text-pt-text-muted">Next:</span>{' '}
                          <span className="font-mono text-pt-text">{clock(next.scheduled_start)}</span>
                          <div className="mt-1 leading-snug text-pt-text-muted">{next.procedure_name}</div>
                        </>
                      ) : (
                        'Idle — no upcoming cases'
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* PACU bed map */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs font-mono uppercase tracking-widest text-pt-gold">PACU Bed Map</h2>
          <span className="text-xs font-mono text-pt-text-muted">
            {pacuCases.length}/{PACU_CAPACITY} occupied
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {beds.map((bed, i) => {
            const occupied = !!bed;
            return (
              <div
                key={i}
                className={`rounded-lg border p-4 ${
                  occupied ? 'bg-pt-amber/5 border-pt-amber/40' : 'bg-pt-dark border-pt-border'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <IconHospitalBed size={20} className={occupied ? 'text-pt-amber' : 'text-pt-text-dim'} />
                    <span className="font-mono text-xs text-pt-text-muted">Bed {i + 1}</span>
                  </div>
                  {occupied && <IconVitalsMonitor size={16} className="text-pt-green" />}
                </div>
                {bed ? (
                  <Link to={`/live-cases/${bed.case_id}`} className="block group">
                    <p className="text-sm font-semibold text-pt-text group-hover:text-pt-gold transition-colors leading-snug">
                      {bed.patient_name}
                    </p>
                    <div className="text-xs text-pt-text-muted mt-1 leading-snug">{bed.procedure_name}</div>
                    <div className="flex items-center gap-1 mt-3 pt-3 border-t border-pt-border text-[11px] font-mono text-pt-text-muted">
                      <Clock className="w-3 h-3" />
                      In PACU {elapsed(bed.stage_entered_at)}
                    </div>
                  </Link>
                ) : (
                  <p className="text-xs text-pt-text-dim">Available</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
